import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { defaultPagingInfo } from '../../../types';
import { areEqual } from '../../../utils';
import { CloseIconButton } from '../../common/Buttons';
import { OpenDefaultMapButton, PageTitle } from '../../common/Jore';
import { Container, Row } from '../../common/LayoutComponents';
import { StopsByLineSearchResults } from './ByLine';
import { StopSearchByStopResults } from './ByStop';
import { StopSearchBar } from './Components';
import { StopAreaSearchResults } from './ForStopAreas/StopAreaSearchResults';
import { TerminalSearchResults } from './ForTerminals/TerminalSearchResults';
import {
  SearchBy,
  SearchFor,
  StopSearchFilters,
  defaultFilters,
  defaultResultSelection,
  defaultSortingInfo,
} from './Types';
import {
  SearchRouterStateContextProvider,
  trSearchFor,
  useStopSearchRouterState,
} from './Utils';

const testIds = {
  container: 'StopSearchResultsPage::Container',
  closeButton: 'StopSearchResultsPage::closeButton',
};

const NoFiltersHeader: FC = () => {
  const { t } = useTranslation();

  return (
    <Row className="items-end justify-between">
      <PageTitle.H1>{t(($) => $.stops.stops)}</PageTitle.H1>
      <OpenDefaultMapButton />
    </Row>
  );
};

type ActiveFiltersHeaderProps = {
  readonly resetSearch: () => void;
  readonly searchFor: SearchFor;
};

const ActiveFiltersHeader: FC<ActiveFiltersHeaderProps> = ({
  resetSearch,
  searchFor,
}) => {
  const { t } = useTranslation();

  return (
    <Row className="items-end justify-between">
      <PageTitle.H1>
        {t(($) => $.search.searchResultsTitleFor, {
          for: trSearchFor(t, searchFor),
        })}
      </PageTitle.H1>
      <CloseIconButton
        className="text-lg font-bold text-brand"
        label={t(($) => $.close)}
        onClick={resetSearch}
        testId={testIds.closeButton}
      />
    </Row>
  );
};

const Results: FC<{ readonly filters: StopSearchFilters }> = ({ filters }) => {
  if (filters.searchBy === SearchBy.Line) {
    return <StopsByLineSearchResults />;
  }

  if (filters.searchFor === SearchFor.StopAreas) {
    return <StopAreaSearchResults />;
  }

  if (filters.searchFor === SearchFor.Terminals) {
    return <TerminalSearchResults />;
  }

  return <StopSearchByStopResults />;
};

function resultWillBeGrouped(filters: StopSearchFilters): boolean {
  return (
    filters.searchFor !== SearchFor.Stops || filters.searchBy === SearchBy.Line
  );
}

const StopSearchResultPageImpl: FC = () => {
  const {
    state: { filters, pagingInfo },
    historyState,
    setHistoryState,
    setSearchState,
  } = useStopSearchRouterState();

  const hasActiveFilters = !areEqual(defaultFilters, filters);

  const onSubmitFilters = (nextFilters: StopSearchFilters) => {
    setSearchState({
      ...defaultFilters,
      ...nextFilters,
      ...defaultSortingInfo,
      ...defaultPagingInfo,
      pageSize: pagingInfo.pageSize,
    });
    setHistoryState((p) => ({
      ...p,
      resultSelection: defaultResultSelection,
      selectedGroups: [],
      knownStopIds: resultWillBeGrouped(nextFilters)
        ? { listingMode: 'grouped', ids: [], groups: {} }
        : { listingMode: 'flat', ids: [] },
    }));
  };

  return (
    <Container testId={testIds.container}>
      {hasActiveFilters ? (
        <ActiveFiltersHeader
          resetSearch={() =>
            setSearchState({
              ...defaultFilters,
              ...defaultSortingInfo,
              ...defaultPagingInfo,
            })
          }
          searchFor={filters.searchFor}
        />
      ) : (
        <NoFiltersHeader />
      )}

      <StopSearchBar
        initialFilters={filters}
        searchIsExpanded={historyState.searchIsExpanded}
        toggleSearchIsExpanded={() =>
          setHistoryState((p) => ({
            ...p,
            searchIsExpanded: !p.searchIsExpanded,
          }))
        }
        onSubmit={onSubmitFilters}
      />

      {hasActiveFilters && <Results filters={filters} />}
    </Container>
  );
};

export const StopSearchResultPage: FC = () => {
  return (
    <SearchRouterStateContextProvider>
      <StopSearchResultPageImpl />
    </SearchRouterStateContextProvider>
  );
};
